import { DeviceLocation, ReplaceDeviceRequest } from 'common';
import { HttpService, HttpServiceResult } from './http.service';
import { AxiosError } from 'axios';
import { FormControlError } from '@/composables/use-form-control';
import { DeviceWarrantyInfo, MeterWarrantyInfo } from '@ncss/models';

export type ReplaceDeviceResult =
  | { success: true; device: DeviceLocation; errors?: FormControlError[] }
  | { success: false; device?: DeviceLocation; errors: FormControlError[] };

interface DeviceTreeInfo {
  childrenCount?: number;
  firstHopId?: number;
  hopPath?: Array<{ id: number; parentId?: number }>;
  hopCount?: number;
}

export class DeviceService extends HttpService {
  findDevice(deviceId: number): Promise<DeviceLocation> {
    return this.send<DeviceLocation>({
      method: 'get',
      url: `/Devices/${deviceId}`,
    });
  }

  async validateReplaceDevice(data: ReplaceDeviceRequest): Promise<FormControlError[]> {
    try {
      await this.send({
        method: 'put',
        url: '/ReplaceDevice/Validate',
        data,
      });
    } catch (e) {
      if (e instanceof AxiosError && e?.response?.data?.issues) {
        return e.response.data.issues.map((i: any) => ({ msg: i.reason }));
      }
    }
    return [];
  }

  async replaceDevice(data: ReplaceDeviceRequest): Promise<ReplaceDeviceResult> {
    try {
      const device = await this.send<DeviceLocation>({
        method: 'put',
        url: '/ReplaceDevice',
        data,
      });
      return {
        success: true,
        device,
      };
    } catch (e) {
      return {
        success: false,
        errors:
          e instanceof AxiosError && e?.response?.data?.issues
            ? (e.response.data.issues as any[]).map((i) => ({ msg: i.reason }))
            : [{ msg: 'Failed to replace device' }],
      };
    }
  }

  async getDeviceWarrantyInfo(deviceId: string | number): Promise<HttpServiceResult<DeviceWarrantyInfo>> {
    if (typeof deviceId === 'string') {
      deviceId = parseInt(deviceId, 16);
    }
    try {
      const warranty = await this.send<DeviceWarrantyInfo>({
        method: 'get',
        url: `/Warranty/Devices/${deviceId}`,
      });
      return {
        success: true,
        data: warranty,
      };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to retreive warranty info',
      };
    }
  }

  async getMeterWarrantyInfo(deviceId: string | number): Promise<HttpServiceResult<MeterWarrantyInfo>> {
    if (typeof deviceId === 'string') {
      deviceId = parseInt(deviceId, 16);
    }
    try {
      const warranty = await this.send<MeterWarrantyInfo>({
        method: 'get',
        url: `/Warranty/Meters/${deviceId}`,
      });
      return {
        success: true,
        data: warranty,
      };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to retrieve meter warranty info',
      };
    }
  }

  async getDeviceManufacturingImage(deviceId: string | number) {
    if (typeof deviceId === 'string') {
      deviceId = parseInt(deviceId, 16);
    }
    try {
      const image = await this.send<{ link: string; date: string }>({
        method: 'get',
        url: `/ManufacturingImage/${deviceId}`,
      });
      return {
        success: true,
        image,
      };
    } catch (e) {
      if (e instanceof AxiosError && e?.response?.data?.issues) {
        return {
          success: false,
          errors: e.response.data.issues.map((i: any) => ({ msg: i.reason })),
        };
      } else {
        throw e;
      }
    }
  }

  async getDeviceTreeInfo(deviceId: number) {
    try {
      const res = await this.send<DeviceTreeInfo>({ method: 'get', url: `/Devices/${deviceId}/DeviceTree` });
      return res;
    } catch (e) {
      return {};
    }
  }
}
